import styled, { css } from 'styled-components';

import { mq } from 'components/constants/breakpoints';

import * as Q from './TaskQuick.styled';
import { BUCKET_TONES, chipTone, ITaskTone, SURFACE } from './taskTones';

/* Цвета — из `taskTones.ts`, общего с доской и Гантом. Пока этот файл держал
 * свои литералы, высокий приоритет был здесь ЯНТАРНЫМ, а на карточке доски —
 * красным; «в работе» — #1d4ed8 здесь и #1e3a8a там. */

export const SrOnly = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

export const List = styled.div<{ $fill?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 14px;

  ${({ $fill }) =>
    $fill &&
    css`
      flex: 1;
      min-height: 0;
      overflow-y: auto;
      padding-right: 4px;
    `}
`;

export const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const SectionHead = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--k-fg-475569);
`;

export const SectionToggle = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  border: 0;
  background: none;
  padding: 0;
  cursor: pointer;
  font: inherit;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--k-fg-475569);

  &:hover {
    color: var(--k-fg-0f172a);
  }
`;

export const SectionCount = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 18px;
  padding: 0 6px;
  border-radius: 9px;
  background: var(--k-bg-f1f5f9);
  color: var(--k-fg-475569);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0;
`;

export const SectionNote = styled.span`
  font-size: 11px;
  font-weight: 500;
  text-transform: none;
  letter-spacing: 0;
  color: var(--k-fg-94a3b8);
`;

export const GroupHead = styled.div<{ $tone: ITaskTone }>`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 4px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: ${({ $tone }) => $tone.fg};
`;

export const Rows = styled.ul<{ over?: boolean }>`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
  border-radius: 8px;
  min-height: 8px;
  background: ${({ over }) => (over ? 'var(--k-bg-eff6ff)' : 'transparent')};
  transition: background 0.12s;
`;

/* --- The row -------------------------------------------------------------
 * Handle, checkbox and title are SIBLINGS — never nested. The title button
 * stretches over the whole row via ::after, so clicking anywhere but the two
 * controls opens the details drawer. */

/**
 * Ручка перетаскивания.
 *
 * ТЗ 0063: раньше это была бледная иконка в 20px цветом `#cbd5e1` — владелец
 * решил, что перетаскивание вообще не работает, хотя оно работало: он просто
 * не попадал в цель. Ручка осталась ОТДЕЛЬНЫМ элементом (корпус строки тянуть
 * нельзя: react-beautiful-dnd отказывается начинать перетаскивание с вложенных
 * контролов, и ровно из-за этого её когда-то и вынесли), но стала крупнее,
 * контрастнее и с собственным фоном при наведении.
 */
export const DragHandle = styled.button`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 28px;
  border: 0;
  border-radius: 6px;
  background: none;
  padding: 0;
  cursor: grab;
  color: var(--k-fg-94a3b8);
  transition: color 0.12s ease, background 0.12s ease;

  &:hover,
  &:focus-visible {
    color: var(--k-fg-1d4ed8);
    background: var(--k-bg-eff6ff);
  }

  &:active {
    cursor: grabbing;
  }
`;

export const Row = styled.li<{ $done?: boolean; $dragging?: boolean }>`
  position: relative;
  display: grid;
  /* Первая колонка — под ручку 24px (ТЗ 0063). У названия есть НИЖНИЙ предел:
     ТЗ 0065 положило в правую колонку до трёх чипов связей и полосу быстрых
     действий, а трек auto сайзится по содержимому — название сжималось
     вплоть до нуля, и от строки оставались одни чипы. */
  grid-template-columns: 24px 18px minmax(120px, 1fr) minmax(0, auto);
  align-items: center;
  gap: 8px;
  padding: 7px 10px 7px 2px;
  border: 1px solid
    ${({ $dragging }) =>
      $dragging ? 'var(--k-border-93c5fd)' : 'var(--k-border-e2e8f0)'};
  border-radius: 8px;
  background: ${({ $dragging }) =>
    $dragging ? 'var(--k-bg-eff6ff)' : 'var(--k-bg-ffffff)'};
  box-shadow: ${({ $dragging }) =>
    $dragging ? '0 6px 18px var(--k-shadow-0f172a-a120)' : 'none'};

  &:hover {
    border-color: var(--k-border-cbd5e1);
  }

  /* Строка при наведении сама показывает, что её можно тянуть. */
  &:hover ${DragHandle} {
    color: var(--k-fg-475569);
  }

  &:hover ${Q.Actions}, &:focus-within ${Q.Actions} {
    visibility: visible;
    opacity: 1;
  }

  ${({ $done }) =>
    $done &&
    css`
      opacity: 0.62;
    `}

  /* ТЗ 0121, телефон: чекбокс и название — первой строкой, мета и быстрые
     действия — второй, во всю ширину. Ручки нет (на сенсоре перетаскивания
     нет), её пустая ячейка не занимает места. */
  ${mq.mobile} {
    grid-template-columns: 24px minmax(0, 1fr);
    row-gap: 6px;
    padding: 10px 12px;

    > :first-child {
      display: none;
    }
  }
`;

export const RowCheck = styled.input`
  position: relative;
  z-index: 1;
  width: 16px;
  height: 16px;
  margin: 0;
  cursor: pointer;
  accent-color: var(--k-fg-2563eb);

  ${mq.mobile} {
    width: 22px;
    height: 22px;
  }
`;

export const RowTitle = styled.button<{ $done?: boolean }>`
  border: 0;
  background: none;
  padding: 0;
  text-align: left;
  font: inherit;
  font-size: 13px;
  color: var(--k-fg-0f172a);
  cursor: pointer;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  /* ТЗ 0121: на телефоне название переносится — обрезанное многоточием
     на 300 px оно не читается. */
  ${mq.mobile} {
    font-size: 15px;
    white-space: normal;
    min-height: 24px;
  }
  text-decoration: ${({ $done }) => ($done ? 'line-through' : 'none')};

  /* Stretched link: the whole row is the hit area, but there is exactly ONE
     button here — no nested interactive elements. */
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 8px;
  }

  &:focus-visible::after {
    outline: 2px solid var(--k-border-2563eb);
    outline-offset: -2px;
  }
`;

export const RowMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  overflow: hidden;

  /* ТЗ 0121, телефон: своей строкой во всю ширину строки задачи. */
  ${mq.mobile} {
    grid-column: 1 / -1;
    flex-wrap: wrap;
    overflow: visible;
  }
`;

export const OverdueTag = styled.span`
  display: inline-flex;
  align-items: center;
  height: 18px;
  padding: 0 6px;
  border-radius: 4px;
  background: ${BUCKET_TONES.overdue.bg};
  color: ${BUCKET_TONES.overdue.fg};
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.02em;
`;

export const RowDue = styled.span<{ $overdue?: boolean }>`
  font-size: 11px;
  font-variant-numeric: tabular-nums;
  /* Поле fg, а не line — то же самое, что у карточки доски. Разъехались они
     МОЛЧА и сразу: файл тонов завели, чтобы один признак не был покрашен
     дважды, и в том же коммите просрочка стала #b91c1c на доске и #dc2626 в
     списке. Тест тонов теперь сверяет ВЫЧИСЛЕННЫЙ цвет обоих элементов, а не
     содержимое карт. (Обратные кавычки внутри styled-шаблона обрывают его.) */
  color: ${({ $overdue }) =>
    $overdue ? BUCKET_TONES.overdue.fg : SURFACE.muted};
`;

export const RowChip = styled.span<{ tone?: 'accent' | 'high' }>`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  height: 18px;
  padding: 0 6px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  background: ${({ tone }) => chipTone(tone).bg};
  color: ${({ tone }) => chipTone(tone).fg};
`;

export const Empty = styled.div`
  font-size: 13px;
  color: var(--k-fg-94a3b8);
  padding: 10px 2px;
`;

/* --- Quick add ----------------------------------------------------------- */

export const QuickAdd = styled.div<{ $compact?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  /* В списке строка выровнена по названиям (отступ под ручку и чекбокс);
     в колонке доски она стоит на месте карточки и отступа не имеет. */
  padding: ${({ $compact }) => ($compact ? '0' : '4px 10px 4px 30px')};
`;

export const QuickAddInput = styled.input`
  flex: 1;
  min-width: 0;
  border: 1px dashed var(--k-border-cbd5e1);
  border-radius: 8px;
  padding: 7px 10px;
  font: inherit;
  font-size: 13px;
  background: transparent;

  &::placeholder {
    color: var(--k-fg-94a3b8);
  }

  &:focus {
    border-style: solid;
    border-color: var(--k-border-2563eb);
    background: var(--k-bg-ffffff);
    outline: none;
  }
`;

/* --- Toolbar ------------------------------------------------------------- */

export const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  /* 12 → 8px: строка фильтров и полотно задач — соседи, а не разделы. */
  margin-bottom: 8px;
  min-width: 0;
`;

export const Spacer = styled.div`
  flex: 1;
  min-width: 0;

  /* ТЗ 0121: на телефоне строки переносятся, распорка лишь рвала бы их. */
  ${mq.mobile} {
    display: none;
  }
`;

/** Ряд чипов фильтра ПЕРЕНОСИТСЯ (ТЗ 0111, проверка глазами): в одну
 *  строку «Local SalesManager» и «Оператор дофинансирования» уезжали за
 *  край окна фильтров. */
export const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  min-width: 0;
`;

/**
 * Быстрые фильтры над задачами (ТЗ 0121). На компьютере — как `ChipRow`; на
 * телефоне одна строка, листаемая вбок: четыре чипа в два ряда съедали бы
 * экран, а переполнять страницу нельзя.
 */
export const QuickChipRow = styled(ChipRow)`
  ${mq.mobile} {
    flex: 1 1 100%;
    flex-wrap: nowrap;
    overflow-x: auto;
    scrollbar-width: none;
    -webkit-overflow-scrolling: touch;

    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

export const FilterChip = styled.button<{ active?: boolean }>`
  border: 1px solid
    ${({ active }) =>
      active ? 'var(--k-border-2563eb)' : 'var(--k-border-e2e8f0)'};
  background: ${({ active }) =>
    active ? 'var(--k-bg-eff6ff)' : 'var(--k-bg-ffffff)'};
  color: ${({ active }) =>
    active ? 'var(--k-fg-1d4ed8)' : 'var(--k-fg-475569)'};
  border-radius: 999px;
  padding: 4px 10px;
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  /* Подпись не ломается внутри чипа («В / работе»); слишком длинная —
     с многоточием, полный текст — в title. */
  white-space: nowrap;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;

  /* ТЗ 0121: зона нажатия на телефоне — 40 px. */
  ${mq.mobile} {
    min-height: 40px;
    padding: 0 14px;
    font-size: 13px;
    flex-shrink: 0;
  }

  &:hover:not(:disabled) {
    border-color: var(--k-border-cbd5e1);
  }

  /* Недоступный чип должен ВЫГЛЯДЕТЬ недоступным: иначе «не нажимается»
     читается как поломка, а не как запрет. */
  &:disabled {
    opacity: 0.45;
    cursor: default;
  }
`;

/* --- Deadline board: collapsible sections below 1280px ------------------- */

export const StackHead = styled.button<{ $tone: ITaskTone }>`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  border: 0;
  background: none;
  padding: 8px 4px;
  cursor: pointer;
  font: inherit;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${({ $tone }) => $tone.fg};
`;

/* --- Поповер фильтров (ТЗ 0062) ------------------------------------------ */

export const FilterPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  /* Не шире экрана: на узком окне панель упиралась бы в край. */
  width: min(380px, calc(100vw - 64px));
  max-width: 100%;
  min-width: 0;
`;

export const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

/** Постоянная подсказка под контролом — не подсказка по наведению.
 *  Правило «внутри вида — любой, между видами — одновременно» неочевидно, и
 *  узнавать его, наводя мышь, человек не станет. */
export const FilterHint = styled.div`
  margin-top: 6px;
  font-size: 11px;
  line-height: 1.4;
  color: var(--k-fg-94a3b8);
`;

export const FilterGroupLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--k-fg-94a3b8);
`;
